<template>
  <div class="rounded-lg relative block leading-none h-full">
    <RevButtonBase
      ref="interactiveElement"
      :aria-label
      class="rounded-lg absolute inset-0 flex items-center justify-center h-full"
      @click.stop="onStop"
      @focusin="isHovering = true"
      @focusout="isHovering = false"
      @keydown.enter.stop="onStop"
      @mouseenter="isHovering = true"
      @mouseleave="isHovering = false"
    >
      <RevIllustration
        v-if="!isPlaying && !videoStarted"
        :alt="title"
        class="rounded-lg h-full object-cover"
        :class="imgClassName"
        :height="360"
        image-class-names=""
        :src="poster"
        :width="480"
      />
      <div
        class="rounded-full absolute left-1/2 top-1/2 flex h-[68px] w-[68px] -translate-x-1/2 -translate-y-1/2 items-center justify-center"
        :class="{
          'bg-[#FFF]': !isPlaying || isHovering,
        }"
      >
        <IconMediaPlay v-if="!isPlaying || isVideoEnded" size="48" />
        <IconMediaPause v-else-if="isHovering" size="48" />
      </div>
    </RevButtonBase>
    <video
      v-if="isPlaying || videoStarted"
      ref="videoElement"
      autoplay
      class="rounded-lg h-full w-full object-cover"
      :class="imgClassName"
      muted
      playsinline
      :poster="posterSrc.src"
      @ended="onEnded"
      @error="onError"
      @playing="onPlaying"
    >
      <source
        v-for="source in sources"
        :key="source.src"
        :src="source.src"
        :type="source.type"
      />
    </video>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, useTemplateRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevIllustration } from '@ds/components/Illustration'
import { IconMediaPause } from '@ds/icons/IconMediaPause'
import { IconMediaPlay } from '@ds/icons/IconMediaPlay'
import { useIllustrationOptimizer } from '@ds/plugin'
import { useIntersectionObserver } from '@vueuse/core'

import { MARKETING_VIDEO_TITLE } from './MarketingVideo.constants'
import translations from './MarketingVideo.translations'
import type { VideoSource } from './MarketingVideo.types'

const {
  sources,
  productName,
  poster,
  title = MARKETING_VIDEO_TITLE,
  isMobile,
  imgClassName = 'aspect-square',
} = defineProps<{
  // Ordered list of <source> entries (webm first, mp4 fallback).
  sources: VideoSource[]
  productName: string | undefined
  // Poster/thumbnail shown before playback.
  poster: string
  title?: string
  // Only used for deciding the autoplay behavior (mobile vs desktop).
  isMobile: boolean
  imgClassName?: string
}>()

const emit = defineEmits<{
  (e: 'click'): void
  (e: 'start'): void
}>()

// The native <video poster> attribute can't go through <RevIllustration>, so
// it needs its own CDN-resolved URL (same optimizer, single flat src).
const posterSrc = useIllustrationOptimizer(() => poster)

const isPlaying = ref(false)
const isHovering = ref(false)
const videoStarted = ref(false)
const isVideoEnded = ref(false)
const hasEmittedStart = ref(false)

const i18n = useI18n()
// TODO(PPI-7872): the pause aria-label has no translation yet, so it's
// disabled for now and the control always exposes the "Play" label, even
// while playing. Restore the isPlaying/pauseAriaLabel branch once translated.
const ariaLabel = computed(() =>
  i18n(translations.playAriaLabel, { productName }),
)

const videoElement = useTemplateRef('videoElement')

// The audio track is stripped from the encoded files (muted playback only), so
// `muted` is always safe and is required for inline autoplay. We also set it
// imperatively because Vue's `muted` attribute binding is unreliable.
function playVideo({ fromStart = false } = {}) {
  isPlaying.value = true
  videoStarted.value = true
  isVideoEnded.value = false

  const element = videoElement.value
  if (element) {
    element.muted = true
    if (fromStart) {
      element.currentTime = 0
    }
    void element.play().catch(() => {
      // Browser autoplay policy may reject playback; safe to ignore.
    })
  }
  // On the first call the <video> isn't mounted yet; the `autoplay` attribute
  // starts playback as soon as it mounts (the equivalent of YouTube's
  // `autoplay=1` baked into the iframe src).
}

function pauseVideo() {
  isPlaying.value = false
  videoElement.value?.pause()
}

function onPlaying() {
  // Mirror the single `@load="emit('start')"` on the YouTube iframe.
  if (!hasEmittedStart.value) {
    hasEmittedStart.value = true
    emit('start')
  }
}

function onStop() {
  if (isPlaying.value) {
    pauseVideo()
  } else {
    playVideo()
  }
  emit('click')
}

const { trackVideoImpression } = useTracking()

function onEnded() {
  isPlaying.value = false
  isVideoEnded.value = true

  trackVideoImpression({
    name: 'video_completed',
    pageType: 'product',
    zone: 'image_carousel',
  })
}

function onError() {
  // Graceful fallback: drop back to the poster + play button.
  isPlaying.value = false
  videoStarted.value = false
}

const interactiveElement = useTemplateRef('interactiveElement')
const { stop: stopIntersectionObserver } = useIntersectionObserver(
  interactiveElement,
  ([{ isIntersecting, intersectionRatio }]) => {
    if (isIntersecting && intersectionRatio > 0.7 && isMobile) {
      if (!isPlaying.value) {
        playVideo({ fromStart: true })
      }

      stopIntersectionObserver()
    } else if (isPlaying.value && isMobile) {
      pauseVideo()
    }

    if (isIntersecting && intersectionRatio > 0.9 && !isMobile) {
      if (!isPlaying.value) {
        playVideo({ fromStart: !videoStarted.value })
      }
    } else if (isPlaying.value && !isMobile) {
      pauseVideo()
    }
  },
  {
    threshold: 0.7,
  },
)

// Ensure the element is muted as soon as it is rendered, so the `autoplay`
// attribute is allowed to start playback under browser autoplay policies.
const stopWatchingVideoElement = watch(videoElement, () => {
  if (videoElement.value) {
    videoElement.value.muted = true
  }
})

let hoveringTimeout: ReturnType<typeof setTimeout>
const stopWatchingHovering = watch(isHovering, (isHoveringValue) => {
  if (isHoveringValue) {
    clearTimeout(hoveringTimeout)
    // we need to reset the hovering state after X seconds
    // because on mobile we don't fall in the `mouseleave` case
    hoveringTimeout = setTimeout(() => {
      isHovering.value = false
    }, 2000)
  }
})

onUnmounted(() => {
  clearTimeout(hoveringTimeout)
  stopWatchingHovering()
  stopWatchingVideoElement()
  stopIntersectionObserver()
})

defineExpose({
  playVideo,
})
</script>
